import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { goalsApi } from "@/api/goals";
import type { GoalProgress } from "@/api/goals";
import { queryKeys } from "@/lib/query-keys";
import { MarkdownRenderer } from "@/components/chat/markdown-renderer";
import { Target, Plus, Trash2 } from "lucide-react";

const statusVariant: Record<string, "success" | "warning" | "default"> = {
  active: "warning",
  in_progress: "default",
  completed: "success",
  abandoned: "secondary" as "default",
};

interface GoalCreateForm {
  title: string;
  description: string;
  ownerId: string;
  targetDate: string;
  recurring: boolean;
  cronExpression: string;
  timezone: string;
}

const emptyForm: GoalCreateForm = {
  title: "", description: "", ownerId: "", targetDate: "",
  recurring: false, cronExpression: "0 9 * * *", timezone: "UTC",
};

function ProgressBar({ goalId }: { goalId: string }) {
  const { data: progress } = useQuery({
    queryKey: queryKeys.goals.progress(goalId),
    queryFn: () => goalsApi.progress(goalId),
  });

  if (!progress || progress.total === 0) return null;

  return (
    <div className="mt-2 flex items-center gap-2">
      <div className="h-1.5 flex-1 rounded-full bg-surface-hover overflow-hidden">
        <div
          className="h-full rounded-full bg-accent transition-all"
          style={{ width: `${progress.percentComplete}%` }}
        />
      </div>
      <span className="text-[11px] text-text-muted whitespace-nowrap">
        {(progress.byStatus["done"] ?? 0)} of {progress.total} completed
      </span>
    </div>
  );
}

export function Goals() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const { data: goals = [], isLoading } = useQuery({
    queryKey: queryKeys.goals.byCompany(tenantId),
    queryFn: () => goalsApi.list(tenantId),
  });
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<GoalCreateForm>(emptyForm);
  const [titleTouched, setTitleTouched] = useState(false);

  const createMutation = useMutation({
    mutationFn: () =>
      goalsApi.create({
        companyId: tenantId,
        title: form.title,
        description: form.description || undefined,
        ownerId: form.ownerId || null,
        targetDate: form.targetDate ? new Date(form.targetDate).toISOString() : null,
        ...(form.recurring && form.ownerId ? {
          cronExpression: form.cronExpression,
          timezone: form.timezone,
        } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.byCompany(tenantId) });
      setShowCreate(false);
      setForm(emptyForm);
      setTitleTouched(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => goalsApi.update(id, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.goals.byCompany(tenantId) }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => goalsApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.goals.byCompany(tenantId) }),
  });

  const agentName = (ownerId: string | null) => {
    if (!ownerId) return null;
    const agent = agents.find((a) => a.id === ownerId);
    return agent?.name ?? null;
  };

  return (
    <CenterPanel title="Goals">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-text-secondary">{goals.length} goal{goals.length !== 1 ? "s" : ""}</p>
        <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
          <Plus size={16} className="mr-1.5" />
          New Goal
        </Button>
      </div>

      {showCreate && (
        <Card className="mb-4">
          <CardContent className="space-y-3 pt-4">
            <div>
              <Input
                placeholder="Goal title *"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                onBlur={() => setTitleTouched(true)}
                className={titleTouched && !form.title.trim() ? "border-danger" : ""}
              />
              {titleTouched && !form.title.trim() && (
                <p className="text-xs text-danger mt-1">Goal title is required.</p>
              )}
            </div>
            <textarea
              placeholder="Description (optional)"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm min-h-[80px] resize-y"
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs text-text-muted mb-1 block">Assigned Agent</label>
                <select
                  value={form.ownerId}
                  onChange={(e) => setForm({ ...form, ownerId: e.target.value })}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
                >
                  <option value="">Unassigned</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-text-muted mb-1 block">Target Date</label>
                <Input
                  type="date"
                  value={form.targetDate}
                  onChange={(e) => setForm({ ...form, targetDate: e.target.value })}
                />
              </div>
            </div>
            {form.ownerId && (
              <div>
                <label className="flex items-center gap-2 text-xs text-text-muted cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.recurring}
                    onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
                    className="rounded"
                  />
                  Recurring (schedule as cron task)
                </label>
                {form.recurring && (
                  <div className="grid grid-cols-1 gap-3 mt-2 sm:grid-cols-2">
                    <select
                      value={form.cronExpression}
                      onChange={(e) => setForm({ ...form, cronExpression: e.target.value })}
                      className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
                    >
                      <option value="0 * * * *">Every hour</option>
                      <option value="0 9 * * *">Daily at 09:00</option>
                      <option value="0 9 * * 1-5">Weekdays at 09:00</option>
                      <option value="0 0 * * 1">Weekly (Monday)</option>
                      <option value="0 0 1 * *">Monthly (1st)</option>
                    </select>
                    <select
                      value={form.timezone}
                      onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                      className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
                    >
                      <option value="UTC">UTC</option>
                      <option value="Europe/London">London</option>
                      <option value="America/New_York">New York</option>
                    </select>
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <Button size="sm" disabled={!form.title.trim() || createMutation.isPending} onClick={() => createMutation.mutate()}>
                Create
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowCreate(false); setForm(emptyForm); setTitleTouched(false); }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading goals...</p>
      ) : goals.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Target size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">No goals yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {goals.map((goal) => (
            <Card
              key={goal.id}
              className="cursor-pointer hover:border-accent/50 transition-colors"
              onClick={() => navigate(`/tenants/${tenantId}/goals/${goal.id}`)}
            >
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary">{goal.title}</p>
                    {goal.description && (
                      <div className="text-xs text-text-muted mt-0.5 line-clamp-2"><MarkdownRenderer content={goal.description} /></div>
                    )}
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-text-muted">
                      {agentName(goal.ownerId) && (
                        <span>Agent: {agentName(goal.ownerId)}</span>
                      )}
                      {goal.targetDate && (
                        <span>Target: {new Date(goal.targetDate).toLocaleDateString()}</span>
                      )}
                    </div>
                    <ProgressBar goalId={goal.id} />
                  </div>
                  <div className="flex items-center gap-2 ml-3 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Badge variant={statusVariant[goal.status] ?? "default"} className="capitalize">
                      {goal.status.replace("_", " ")}
                    </Badge>
                    {(goal.status === "active" || goal.status === "in_progress") && (
                      <Button size="sm" variant="outline" onClick={() => updateMutation.mutate({ id: goal.id, status: "completed" })}>
                        Complete
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => deleteMutation.mutate(goal.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </CenterPanel>
  );
}
